import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth, getUserFromDatabase } from './firebase';

const UserProfileContext = createContext();

/**
 * Single unified profile completion calculation method
 * Evaluates 12 core profile parameters consistently across Account, Dashboard, and Admin.
 */
export const calculateProfileCompletion = (profile = {}) => {
  if (!profile || typeof profile !== 'object') return 0;

  const role = String(profile.role || profile.userRole || "Student").toLowerCase();
  const isStudent = role.includes("student");

  const fields = [
    profile.name || profile.displayName || profile.fullName,
    profile.username,
    profile.email,
    profile.phone || profile.mobile,
    isStudent ? (profile.stream || profile.designation) : (profile.designation || profile.jobTitle),
    profile.profileImage || profile.photoURL,
    profile.bio || profile.about,
    profile.github || profile.gitHub,
    profile.linkedin || profile.linkedIn,
    profile.institute || profile.company || profile.department,
    profile.state,
    profile.city
  ];

  const filledCount = fields.filter(val => val && String(val).trim().length > 0).length;
  return Math.round((filledCount / fields.length) * 100);
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

export const UserProfileProvider = ({ children, initialUserDetails, uid }) => {
  const [userProfile, setUserProfile] = useState(initialUserDetails || {});
  const [isLoading, setIsLoading] = useState(false);

  // Real-time Firestore sync & immediate update listener
  useEffect(() => {
    const activeUid = uid || initialUserDetails?.uid || (auth && auth.currentUser ? auth.currentUser.uid : null);
    
    if (!activeUid) {
      if (initialUserDetails && Object.keys(initialUserDetails).length > 0) {
        setUserProfile(initialUserDetails);
      }
      return;
    }

    if (initialUserDetails && initialUserDetails.uid === activeUid) {
      setUserProfile(prev => ({ ...prev, ...initialUserDetails }));
    }

    // 1. Subscribe to live Firestore document changes so newly created signup docs populate instantly
    let unsubscribe = () => {};
    try {
      const docRef = doc(db, "users", activeUid);
      unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserProfile(prev => ({
            ...prev,
            ...data,
            uid: activeUid
          }));
        }
      }, (error) => {
        console.warn("[UserProfileContext] Snapshot error:", error);
      });
    } catch (err) {
      console.warn("[UserProfileContext] Realtime listener setup failed:", err);
    }

    // 2. Custom event listener for instant zero-latency signup updates
    const handleProfileUpdated = (e) => {
      if (e.detail && (e.detail.uid === activeUid || !e.detail.uid || !activeUid)) {
        setUserProfile(prev => ({
          ...prev,
          ...e.detail,
          uid: e.detail.uid || activeUid
        }));
      }
    };

    window.addEventListener('slx_user_profile_updated', handleProfileUpdated);

    return () => {
      unsubscribe();
      window.removeEventListener('slx_user_profile_updated', handleProfileUpdated);
    };
  }, [uid, initialUserDetails?.uid]);

  // Function to refresh user data from database manually
  const refreshUserProfile = async () => {
    const targetUid = uid || userProfile?.uid || (auth && auth.currentUser ? auth.currentUser.uid : null);
    if (!targetUid) return;

    setIsLoading(true);
    try {
      const userData = await getUserFromDatabase(targetUid);
      if (userData) {
        setUserProfile(userData);
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to update user profile locally
  const updateUserProfile = (updates) => {
    setUserProfile(prev => ({
      ...prev,
      ...updates
    }));
  };

  const profileCompletion = calculateProfileCompletion(userProfile);

  const value = {
    userProfile,
    setUserProfile,
    updateUserProfile,
    refreshUserProfile,
    profileCompletion,
    calculateProfileCompletion,
    isLoading
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export default UserProfileContext;
